import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { setToken, getToken } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const t = getToken();
      if (!t) { setUser(false); setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setPermissions(data.permissions || []);
      } catch (e) {
        setToken(null);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    setPermissions(data.permissions || []);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logger.warn("Logout API call failed (continuing local sign-out)", e);
    }
    setToken(null);
    localStorage.removeItem("hr_property_id");
    setUser(false);
    setPermissions([]);
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      setPermissions(data.permissions || []);
    } catch (e) {
      logger.error("Failed to refresh permissions", e);
    }
  }, []);

  const hasPermission = useCallback(
    (key) => !key || permissions.includes(key),
    [permissions]
  );

  const value = useMemo(
    () => ({ user, setUser, login, logout, loading, permissions, hasPermission, refreshPermissions }),
    [user, login, logout, loading, permissions, hasPermission, refreshPermissions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
